import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { Button, ExpandingTextarea } from '../../components';

import css from './ReviewResponseForm.module.css';

const MAX_CHARS = 500;

/**
 * A component that renders a form for providers to respond to reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Function} props.onSubmit - Callback when form is submitted with response text
 * @param {boolean} [props.inProgress] - Whether the submit is in progress
 * @returns {JSX.Element}
 */
const ReviewResponseForm = props => {
  const intl = useIntl();
  const { className, rootClassName, onSubmit, inProgress = false } = props;
  const classes = classNames(rootClassName || css.root, className);

  const [responseText, setResponseText] = useState('');
  
  const placeholder = intl.formatMessage({ id: 'ReviewResponseForm.placeholder' });
  const submitLabel = intl.formatMessage({ id: 'ReviewResponseForm.submit' });
  
  const remaining = MAX_CHARS - responseText.length;
  const isOverLimit = remaining < 0;
  const isValid = responseText.trim().length > 0 && !isOverLimit;

  const charLimitMessage = intl.formatMessage(
    { id: 'ReviewResponseForm.charLimit' },
    { remaining: Math.abs(remaining) }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid && onSubmit) {
      onSubmit(responseText.trim());
      setResponseText('');
    }
  };

  const handleTextChange = (e) => {
    setResponseText(e.target.value);
  };

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <ExpandingTextarea
        className={css.textarea}
        value={responseText}
        onChange={handleTextChange}
        placeholder={placeholder}
        rows={3}
        disabled={inProgress}
      />
      <div className={css.formFooter}>
        <div className={classNames(css.charLimit, { [css.overLimit]: isOverLimit })}>
          {charLimitMessage}
        </div>
        <Button
          type="submit"
          size="small"
          disabled={!isValid || inProgress}
          inProgress={inProgress}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default ReviewResponseForm;